import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Activity, TrendingUp, TrendingDown, AlertTriangle, RefreshCw, Shield, Target } from "lucide-react";

const API = "/api";

const EquityCurve = ({ data, stats }) => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    if (!canvasRef.current || !data || data.length < 2) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const width = canvas.width;
    const height = canvas.height;
    const padding = { top: 10, right: 10, bottom: 20, left: 45 };
    
    ctx.clearRect(0, 0, width, height);
    
    const equities = data.map(d => d.equity);
    const times = data.map(d => d.time);
    const minEquity = Math.min(...equities) * 0.995;
    const maxEquity = Math.max(...equities) * 1.005;
    const minTime = Math.min(...times);
    const maxTime = Math.max(...times);
    
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;
    
    const scaleX = (t) => padding.left + ((t - minTime) / (maxTime - minTime || 1)) * chartWidth;
    const scaleY = (e) => padding.top + chartHeight - ((e - minEquity) / (maxEquity - minEquity || 1)) * chartHeight;
    
    ctx.strokeStyle = "#27272A";
    ctx.lineWidth = 0.5;
    for (let i = 0; i <= 4; i++) {
      const y = padding.top + (chartHeight / 4) * i;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();
    }
    
    const initialY = scaleY(stats?.initial_balance || 10000);
    ctx.strokeStyle = "#52525B";
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(padding.left, initialY);
    ctx.lineTo(width - padding.right, initialY);
    ctx.stroke();
    ctx.setLineDash([]);
    
    ctx.strokeStyle = stats?.total_return_pct >= 0 ? "#22C55E" : "#EF4444";
    ctx.lineWidth = 2;
    ctx.beginPath();
    data.forEach((point, i) => {
      const x = scaleX(point.time);
      const y = scaleY(point.equity);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
    
    data.forEach((point) => {
      if (point.trade_id) {
        const x = scaleX(point.time);
        const y = scaleY(point.equity);
        ctx.fillStyle = point.pnl >= 0 ? "#22C55E" : "#EF4444";
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    
    ctx.fillStyle = "#71717A";
    ctx.font = "9px JetBrains Mono, monospace";
    ctx.textAlign = "right";
    for (let i = 0; i <= 4; i++) {
      const equity = minEquity + ((maxEquity - minEquity) / 4) * (4 - i);
      const y = padding.top + (chartHeight / 4) * i;
      ctx.fillText(`$${Math.round(equity)}`, padding.left - 4, y + 3);
    }
  }, [data, stats]);
  
  if (!data || data.length < 2) {
    return <div className="h-20 flex items-center justify-center text-xs text-zinc-600">No trade history</div>;
  }
  
  return <canvas ref={canvasRef} width={260} height={80} style={{ width: "100%", height: "80px" }} data-testid="equity-curve-canvas" />;
};

export const StrategyStatusPanel = ({ currentPrice, onRefresh, openTrades = [] }) => {
  const [state, setState] = useState(null);
  const [settings, setSettings] = useState(null);
  const [equityData, setEquityData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [stateRes, settingsRes, equityRes, healthRes, fibRes] = await Promise.all([
        axios.get(`${API}/strategy/state`, { withCredentials: true }),
        axios.get(`${API}/strategy/settings`, { withCredentials: true }),
        axios.get(`${API}/account/equity-curve`, { withCredentials: true }),
        axios.get(`${API}/health`, { withCredentials: true }),
        axios.get(`${API}/fib-structures`, { withCredentials: true })
      ]);

      const health = healthRes.data || {};
      const bias = health.bias || {};
      const marketBias = health.market_bias || {};
      const permission = health.permission || {};

      const mergedState = {
        ...stateRes.data,
        bias_4h: bias["4H"] || stateRes.data?.bias_4h || null,
        bias_1h: bias["1H"] || stateRes.data?.bias_1h || null,
        bias_15m: bias["15m"] || stateRes.data?.bias_15m || null,
        bias_5m: bias["5m"] || stateRes.data?.bias_5m || null,
        bias_1d: fibRes?.data?.["1D"]?.orientation || null,

        market_bias_15m: marketBias["15m"] || "NEUTRAL",
        market_bias_1h: marketBias["1H"] || "NEUTRAL",
        market_bias_4h: marketBias["4H"] || "NEUTRAL",
        market_bias_1d: marketBias["1D"] || "NEUTRAL",

        permission_4h: permission["4H"] || stateRes.data?.permission_4h || null,
        permission_1h: permission["1H"] || stateRes.data?.permission_1h || null,
        permission_15m: permission["15m"] || stateRes.data?.permission_15m || null,
        permission_5m: permission["5m"] || stateRes.data?.permission_5m || null,
        limits_bias:
          (permission["15m"] === "LONG_ONLY" && "long_only") ||
          (permission["15m"] === "SHORT_ONLY" && "short_only") ||
          stateRes.data?.limits_bias ||
          "no_trade",
      };

      setState(mergedState);
      setSettings(settingsRes.data);
      setEquityData(equityRes.data);
    } catch (e) {
      console.error("Error fetching strategy state:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  if (loading || !state) {
    return <div className="bg-zinc-900 border border-zinc-800 rounded p-3"><div className="text-xs text-zinc-500">Loading LIMITS state...</div></div>;
  }

  const stats = equityData?.stats;
  
  // LIMITS Bias colors
  const getBiasColor = (bias) => {
    if (bias === "long_only") return "text-green-500";
    if (bias === "short_only") return "text-red-500";
    return "text-yellow-500";
  };
  
  const getBiasLabel = (bias) => {
    if (bias === "long_only") return "LONG ONLY";
    if (bias === "short_only") return "SHORT ONLY";
    return "NO TRADE";
  };
  
  const getDirectionIcon = (dir) => {
    if (dir === "up") return <TrendingUp className="w-3 h-3 text-green-500" />;
    if (dir === "down") return <TrendingDown className="w-3 h-3 text-red-500" />;
    return <Activity className="w-3 h-3 text-zinc-500" />;
  };

  const getProtectionState = () => {
    if (openTrades.length === 0) return "FLAT";
    const trade = openTrades[0];
    const entry = Number(trade.entry_price);
    const stop = Number(state.current_stop_level || trade.stop_price);
    const direction = trade.direction;
    if (!entry || !stop || !direction) return "INITIAL";

    if (direction === "LONG") {
      if (stop >= entry) return "BE ACTIVE";
      return "INITIAL";
    }

    if (direction === "SHORT") {
      if (stop <= entry) return "BE ACTIVE";
      return "INITIAL";
    }

    return "INITIAL";
  };

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded" data-testid="strategy-status-panel">
      {/* Fib Structure Bias */}
      <div className="px-3 py-3 border-b border-zinc-800 bg-zinc-950/50">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-zinc-500">Directional Bias</span>
          <span className={`text-xs font-mono font-bold ${getBiasColor(state.limits_bias)}`}>
            {getBiasLabel(state.limits_bias)}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center justify-between p-2 bg-zinc-800/30 rounded">
            <span className="text-xs text-zinc-500">4H</span>
            <div className="flex items-center gap-1">
              {getDirectionIcon((state.bias_4h || "").toLowerCase() === "long" ? "up" : (state.bias_4h || "").toLowerCase() === "short" ? "down" : "")}
              <span className="text-xs font-mono uppercase">{state.bias_4h || "—"}</span>
            </div>
          </div>
          <div className="flex items-center justify-between p-2 bg-zinc-800/30 rounded">
            <span className="text-xs text-zinc-500">1H</span>
            <div className="flex items-center gap-1">
              {getDirectionIcon((state.bias_1h || "").toLowerCase() === "long" ? "up" : (state.bias_1h || "").toLowerCase() === "short" ? "down" : "")}
              <span className="text-xs font-mono uppercase">{state.bias_1h || "—"}</span>
            </div>
          </div>
          <div className="flex items-center justify-between p-2 bg-zinc-800/30 rounded">
            <span className="text-xs text-zinc-500">15M</span>
            <div className="flex items-center gap-1">
              {getDirectionIcon((state.bias_15m || "").toLowerCase() === "long" ? "up" : (state.bias_15m || "").toLowerCase() === "short" ? "down" : "")}
              <span className="text-xs font-mono uppercase">{state.bias_15m || "—"}</span>
            </div>
          </div>
          <div className="flex items-center justify-between p-2 bg-zinc-800/30 rounded">
            <span className="text-xs text-zinc-500">1D</span>
            <div className="flex items-center gap-1">
              {getDirectionIcon((state.bias_1d || "").toLowerCase() === "bullish" ? "up" : (state.bias_1d || "").toLowerCase() === "bearish" ? "down" : "")}
              <span className="text-xs font-mono uppercase">{state.bias_1d ? (state.bias_1d === "bullish" ? "LONG" : "SHORT") : "—"}</span>
            </div>
          </div>
        </div>

      </div>

      {/* Market Structure Bias */}
      <div className="px-3 py-3 border-b border-zinc-800 bg-zinc-950/30">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-zinc-500">Market Bias</span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">
            READ ONLY
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {[
            ["15M", state.market_bias_15m],
            ["1H", state.market_bias_1h],
            ["4H", state.market_bias_4h],
            ["1D", state.market_bias_1d],
          ].map(([tf, bias]) => (
            <div
              key={tf}
              className="flex items-center justify-between p-2 bg-zinc-800/20 rounded"
            >
              <span className="text-xs text-zinc-500">{tf}</span>

              <span className={`text-xs font-mono font-semibold ${
                bias === "BULLISH"
                  ? "text-green-400"
                  : bias === "BEARISH"
                  ? "text-red-400"
                  : "text-yellow-400"
              }`}>
                {bias}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="px-3 py-3 border-b border-zinc-800">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-zinc-500 uppercase tracking-[0.2em]">
            Performance
          </span>

          {stats && (
            <span className={`text-xs font-mono font-semibold ${
              stats.total_return_pct >= 0
                ? 'text-green-400'
                : 'text-red-400'
            }`}>
              {stats.total_return_pct >= 0 ? '+' : ''}
              {stats.total_return_pct}%
            </span>
          )}
        </div>

        {stats && (
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-zinc-600">
                Peak
              </div>
              <div className="text-xs font-mono text-zinc-300">
                ${stats.peak_equity?.toFixed(0)}
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wide text-zinc-600">
                Max DD
              </div>
              <div className={`text-xs font-mono ${
                stats.max_drawdown_pct > 5
                  ? 'text-red-500'
                  : 'text-zinc-300'
              }`}>
                {stats.max_drawdown_pct ?? 0}%
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wide text-zinc-600">
                Closed
              </div>
              <div className="text-xs font-mono text-zinc-300">
                {stats.total_trades}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Trade Lifecycle */}
      <div className="px-3 py-3 border-b border-zinc-800 bg-zinc-950/30">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-zinc-500 uppercase tracking-[0.2em]">
            Trade Lifecycle
          </span>

          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">
            LIVE
          </span>
        </div>

        <div className="space-y-2 max-h-44 overflow-y-auto">

          {openTrades.length > 0 ? (
            <>
              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Now</span>
                <span className="font-mono text-green-400">
                  {openTrades[0].direction} setup active
                </span>
              </div>

              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Status</span>
                <span className="font-mono text-yellow-400">
                  {state.trade_status || "OPEN"}
                </span>
              </div>

              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Engine</span>
                <span className="font-mono text-fuchsia-400">
                  {openTrades[0].engine || "Engine Active"}
                </span>
              </div>

              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Entry</span>
                <span className="font-mono text-zinc-300">
                  ${openTrades[0].entry_price?.toFixed(2) || "—"}
                </span>
              </div>

              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Protection</span>
                <span className="font-mono text-red-400">
                  {getProtectionState()}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Lifecycle</span>
                <span className="font-mono text-zinc-400">
                  Waiting for valid setup
                </span>
              </div>

              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Market</span>
                <span className="font-mono text-green-400">
                  Monitoring structure
                </span>
              </div>

              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Permission</span>
                <span className="font-mono text-yellow-400">
                  {state.permission_15m || "NO_TRADE"}
                </span>
              </div>

              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Engine State</span>
                <span className="font-mono text-zinc-400">
                  Armed standby
                </span>
              </div>
            </>
          )}

        </div>
      </div>
      
      {/* Status */}
      <div className="p-3 space-y-3">

        {/* Active Position Card */}
        {openTrades.length > 0 && (
          <div className={`rounded border p-3 ${
            openTrades[0].direction === "LONG"
              ? "border-green-900/50 bg-green-950/20"
              : "border-red-900/50 bg-red-950/20"
          }`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                Active Position
              </span>

              <span className={`text-xs font-mono font-bold ${
                openTrades[0].direction === "LONG"
                  ? "text-green-400"
                  : "text-red-400"
              }`}>
                {openTrades[0].direction}
              </span>
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Engine</span>
                <span className="font-mono text-zinc-300">
                  {openTrades[0].engine || "—"}
                </span>
              </div>

              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Entry</span>
                <span className="font-mono text-fuchsia-400">
                  ${openTrades[0].entry_price?.toFixed(2) || "—"}
                </span>
              </div>

              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Target</span>
                <span className="font-mono text-green-400">
                  ${openTrades[0].target_price?.toFixed(2) || "—"}
                </span>
              </div>

              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">Stop</span>
                <span className="font-mono text-red-400">
                  {getProtectionState()}
                </span>
              </div>

              <div className="flex justify-between text-xs pt-1 border-t border-zinc-800/60">
                <span className="text-zinc-500">Status</span>
                <span className="font-mono text-yellow-400">
                  {state.trade_status || "OPEN"}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Flat State Card */}
        {openTrades.length === 0 && (
          <div className="rounded border border-zinc-800 bg-zinc-950/70 p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Position Status
              </span>
              <span className="text-xs font-mono font-bold text-zinc-400">
                FLAT
              </span>
            </div>
            <div className="text-xs text-zinc-500 leading-relaxed">
              No active position. Waiting for a valid rule-based entry trigger.
            </div>
          </div>
        )}

        {/* STOP LOSS - Always visible and prominent when in trade */}
        {openTrades.length > 0 && (
          <div className="p-2 bg-red-950/30 border border-red-900/50 rounded">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-3 h-3 text-green-500" />
                <span className="text-xs text-green-400 font-semibold uppercase">Protection State</span>
              </div>
              <span className="text-sm font-mono font-bold text-green-500">
                  ${Number(state.current_stop_level || openTrades[0]?.stop_price)?.toFixed(2) || "—"}
              </span>
            </div>
            <div className="text-xs text-red-400/70 mt-1">
                Stop: ${Number(state.current_stop_level || openTrades[0]?.stop_price)?.toFixed(2) || "—"}
            </div>
          </div>
        )}
        
        {state.last_no_trade_reason && (
          <div className="flex items-center gap-2 p-2 bg-yellow-900/20 border border-yellow-900/30 rounded">
            <AlertTriangle className="w-3 h-3 text-yellow-500 shrink-0" />
            <span className="text-xs text-yellow-500">{state.last_no_trade_reason.replace(/_/g, " ")}</span>
          </div>
        )}
        
        <div className="pt-2 border-t border-zinc-800 space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-500">Protection State</span>
            <span className={`text-xs font-mono font-semibold ${
              getProtectionState() === "FLAT" ? "text-zinc-400" :
              getProtectionState() === "BE ACTIVE" ? "text-green-400" :
              "text-yellow-400"
            }`}>
              {getProtectionState()}
            </span>
          </div>
        </div>
      </div>
      

    </div>
  );
};
